import { CANVAS_SIZE } from "@mn/shared";
import { useEffect, useLayoutEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { useBoard } from "./BoardContext";
import { cssTransform, screenToPage, zoomAt } from "./camera";
import { CardView } from "./cards/CardView";
import { useMapKeys } from "./hooks";
import { Lines } from "./Lines";
import { Presence } from "./Presence";
import { useBoardUI } from "./store";
import { throttle } from "./throttle";

/** The pannable, zoomable viewport. One transformed "world" div holds every card in page coordinates. */
export function Canvas({
  onCreateAt,
  background,
}: {
  onCreateAt: (px: number, py: number) => void;
  background?: string | null;
}) {
  const light = !!background && isLight(background);
  const { handle, index, interactions, canEdit, openBoard } = useBoard();
  const viewportRef = useRef<HTMLDivElement>(null);
  const worldRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const camera = useBoardUI((s) => s.camera);
  const marquee = useBoardUI((s) => s.marquee);
  const setViewport = useBoardUI((s) => s.setViewport);
  const setCamera = useBoardUI((s) => s.setCamera);
  const ids = useMapKeys(handle.types.cards);

  // Camera -> DOM, imperatively, so panning never re-renders the cards.
  useLayoutEffect(() => {
    const w = worldRef.current;
    if (w) w.style.transform = cssTransform(camera);
    const vp = viewportRef.current;
    if (vp) vp.style.setProperty("--zoom", String(camera.z));
    // The dot grid is its own composited layer moved by transform (repainting a full-screen
    // gradient every frame through background-position cost ~12 ms per frame with many cards).
    const g = gridRef.current;
    if (g) {
      const step = 20 * camera.z;
      const ox = ((camera.x * camera.z) % step) - step;
      const oy = ((camera.y * camera.z) % step) - step;
      g.style.transform = `translate(${ox}px, ${oy}px)`;
      g.style.backgroundSize = `${step}px ${step}px`;
    }
  }, [camera]);

  // Viewport size + hand the element to the interaction engine.
  useEffect(() => {
    const el = viewportRef.current!;
    interactions.attach(el);
    const ro = new ResizeObserver(([e]) => e && setViewport(e.contentRect.width, e.contentRect.height));
    ro.observe(el);
    setViewport(el.clientWidth, el.clientHeight);
    return () => ro.disconnect();
  }, [setViewport, interactions]);

  // Wheel: pan; ctrl/cmd+wheel or trackpad pinch: zoom around the cursor.
  useEffect(() => {
    const el = viewportRef.current!;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const sx = e.clientX - r.left;
      const sy = e.clientY - r.top;
      const k = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientHeight : 1;
      const dx = e.deltaX * k;
      const dy = e.deltaY * k;
      if (e.ctrlKey || e.metaKey) {
        const factor = Math.exp(-dy * (e.ctrlKey && Math.abs(dy) < 50 ? 0.01 : 0.002));
        setCamera((c) => zoomAt(c, sx, sy, c.z * factor));
      } else if (e.shiftKey && dx === 0) setCamera((c) => ({ ...c, x: c.x - dy / c.z }));
      else setCamera((c) => ({ ...c, x: c.x - dx / c.z, y: c.y - dy / c.z }));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    const onGesture = (e: Event) => e.preventDefault();
    el.addEventListener("gesturestart", onGesture);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("gesturestart", onGesture);
    };
  }, [setCamera]);

  // Live cursor for collaborators (page coords, throttled).
  const sendCursor = useMemo(
    () =>
      throttle((p: { x: number; y: number } | null) => handle.provider.setAwarenessField("pointer", p), 50),
    [handle],
  );
  useEffect(() => () => sendCursor(null), [sendCursor]);

  // Viewport culling: only mount cards near the screen (plus selected/editing ones).
  const vp = useBoardUI((s) => s.viewport);
  const indexVersion = useSyncExternalStore(index.subscribe, () => index.version);
  const selection = useBoardUI((s) => s.selection);
  const editingId = useBoardUI((s) => s.editingId);
  const visible = useMemo(() => {
    const v = index.visible(camera, vp.w, vp.h);
    for (const id of selection) v.add(id);
    if (editingId) v.add(editingId);
    return v;
    // biome-ignore lint/correctness/useExhaustiveDependencies: indexVersion invalidates the index query
  }, [index, camera, vp, selection, editingId, indexVersion]);

  // Cards being dragged out of a column render at the top level while in flight.
  const draggingMap = useBoardUI((s) => s.dragging);
  const inFlight = draggingMap
    ? [...draggingMap.keys()].filter((id) => handle.types.cards.get(id)?.get("parent"))
    : [];

  const z = camera.z;
  return (
    <div
      ref={viewportRef}
      data-viewport
      className="relative size-full overflow-hidden bg-canvas"
      style={{
        touchAction: "none",
        backgroundColor: background ?? undefined,
        cursor: interactions.spaceDown ? "grab" : undefined,
      }}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget || (e.target as HTMLElement).dataset.world !== undefined)
          interactions.onCanvasPointerDown(e.nativeEvent);
      }}
      onPointerMove={(e) => {
        interactions.onPointerMove(e.nativeEvent);
        const r = e.currentTarget.getBoundingClientRect();
        sendCursor(screenToPage(useBoardUI.getState().camera, e.clientX - r.left, e.clientY - r.top));
      }}
      onPointerUp={(e) => interactions.onPointerUp(e.nativeEvent)}
      onPointerCancel={(e) => interactions.onPointerCancel(e.nativeEvent)}
      onPointerLeave={() => sendCursor(null)}
      onDoubleClick={(e) => {
        // Pointer capture retargets the dblclick to the viewport, so hit-test the real element under the pointer.
        const under = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
        const cardEl = under?.closest("[data-card]") as HTMLElement | null;
        if (cardEl) {
          const id = cardEl.dataset.card!;
          const card = handle.types.cards.get(id);
          const type = card?.get("type") as string | undefined;
          // Board tiles open on double-click (their own handler never fires: the viewport holds pointer capture).
          if (type === "board" || type === "alias") {
            const target = (card?.get("childBoardId") ?? card?.get("targetBoardId")) as string | undefined;
            if (target) openBoard(target);
            return;
          }
          const locked = handle.types.cards.get(id)?.get("locked");
          if (
            type &&
            !locked &&
            ["note", "clone", "heading", "swatch", "sketch", "annotation", "document"].includes(type)
          )
            useBoardUI.getState().setEditing(id);
          return;
        }
        if (!canEdit) return;
        if (under && under !== e.currentTarget && under.dataset.world === undefined) return;
        const r = e.currentTarget.getBoundingClientRect();
        const p = screenToPage(useBoardUI.getState().camera, e.clientX - r.left, e.clientY - r.top);
        onCreateAt(p.x, p.y);
      }}
      onContextMenu={(e) => {
        if (e.target !== e.currentTarget && (e.target as HTMLElement).dataset.world === undefined) return;
        e.preventDefault();
        const r = e.currentTarget.getBoundingClientRect();
        const p = screenToPage(useBoardUI.getState().camera, e.clientX - r.left, e.clientY - r.top);
        useBoardUI
          .getState()
          .setContextMenu({ sx: e.clientX - r.left, sy: e.clientY - r.top, px: p.x, py: p.y, cardId: null });
      }}
    >
      <div
        ref={gridRef}
        aria-hidden
        className="pointer-events-none absolute"
        style={{
          inset: 0,
          width: "calc(100% + 80px)",
          height: "calc(100% + 80px)",
          willChange: "transform",
          backgroundImage: `radial-gradient(circle, ${light ? "#c9c6bf" : "#3a3a3a"} 1px, transparent 1.2px)`,
        }}
      />
      <div
        ref={worldRef}
        data-world
        className="absolute left-0 top-0 h-0 w-0"
        style={{ transformOrigin: "0 0", willChange: "transform" }}
      >
        {ids.map((id) => {
          const m = handle.types.cards.get(id);
          return m && visible.has(id) && !m.get("parent") && !m.get("unsorted") ? (
            <CardView key={id} id={id} ymap={m} />
          ) : null;
        })}
        {inFlight.map((id) => {
          const m = handle.types.cards.get(id);
          return m ? <CardView key={`fly-${id}`} id={id} ymap={m} /> : null;
        })}
        <Lines />
        <Presence />
        <Guides />
      </div>
      {marquee ? (
        <div
          className="pointer-events-none absolute border border-[#8aa9ff] bg-[#8aa9ff]/10"
          style={{
            left: (marquee.x + camera.x) * z,
            top: (marquee.y + camera.y) * z,
            width: marquee.w * z,
            height: marquee.h * z,
          }}
        />
      ) : null}
    </div>
  );
}

/** Perceived brightness of a #rrggbb colour, for picking a dot-grid colour that stays visible. */
export function isLight(hex: string): boolean {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return false;
  const n = Number.parseInt(m[1]!, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b > 150;
}

/** Alignment guide lines while dragging (page coordinates; 1 screen px thick at any zoom). */
function Guides() {
  const guides = useBoardUI((s) => s.guides);
  const z = useBoardUI((s) => s.camera.z);
  if (!guides) return null;
  const L = CANVAS_SIZE;
  return (
    <svg
      className="absolute left-0 top-0"
      width={1}
      height={1}
      style={{ overflow: "visible", pointerEvents: "none", zIndex: 60 }}
      aria-hidden
    >
      {guides.v.map((x) => (
        <line
          key={`v${x}`}
          x1={x}
          y1={-L}
          x2={x}
          y2={L}
          stroke="#ff8ac2"
          strokeWidth={1 / z}
          strokeDasharray={`${4 / z} ${4 / z}`}
        />
      ))}
      {guides.h.map((y) => (
        <line
          key={`h${y}`}
          x1={-L}
          y1={y}
          x2={L}
          y2={y}
          stroke="#ff8ac2"
          strokeWidth={1 / z}
          strokeDasharray={`${4 / z} ${4 / z}`}
        />
      ))}
    </svg>
  );
}
